import { type FC, type MouseEvent, useCallback, useMemo } from "react";
import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";
import { observer } from "mobx-react-lite";
import { isUndefined } from "lodash-es";
import { inflect } from "inflection";
import {
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSTextColor,
  MDSTextOverflow,
  MDSIcon,
  MDSIconKind,
} from "@/mds";
import { SmartCardSection } from "@/components/heads-up/cards/common/layout";
import { CollapsibleSectionHeader } from "@/components/heads-up/cards/common/collapsible-section-header";
import { MDSFadeHorizontalScroll } from "@/mds/components/layout/horizontal-scroll";
import type { CollectionListSmartCard } from "@/components/heads-up/heads-up.types";
import type { Maybe } from "@/domains/common/types";
import type { CollectionListSmartCardCollectionInfo } from "./collection-list-smart-card.types";
import { ZIndex } from "@/domains/design/constants";
import { css } from "@/domains/emotion";
import { useThemedCollectionColors } from "@/mds/themes/use-themed-collection-colors";
import type {
  HeadsUpCardRendererAnalytics,
  HeadsUpCardItemMeta,
} from "@/components/heads-up/cards/registry";
import { HeadsUpItemSource } from "@/components/heads-up/analytics";
import { useHeadsUpClick } from "@/components/heads-up/context/heads-up-context";
import type { HeadsUpClickHandlers } from "@/components/heads-up/context/heads-up-context";

type Props = {
  card: CollectionListSmartCard;
  getCollectionInfo?: (collectionId: string) => Maybe<CollectionListSmartCardCollectionInfo>;
  analytics?: HeadsUpCardRendererAnalytics;
};

export const CollectionListSmartCardRenderer: FC<Props> = ({
  card,
  getCollectionInfo,
  analytics,
}) => {
  const theme = useTheme();
  const clickHandlers = useHeadsUpClick();
  const sectionTitle = card.value.title ?? "";
  const baseItemSource = card.originated_from_find_more
    ? HeadsUpItemSource.FindMore
    : HeadsUpItemSource.Standard;

  const items = useMemo(
    () =>
      card.value.items.map((item, index) => ({
        id: item.collection_id,
        primary: item.primary_label ?? "Untitled collection",
        secondary: item.secondary_label,
        positionIndex: index,
      })),
    [card]
  );

  return (
    <BorderlessSmartCardSection>
      <PaddedCollapsibleSectionHeader title={sectionTitle}>
        <Carousel
          variant="inverted"
          scrollButtonClassName={scrollButtonClassName}
          fadeColor={theme.colors.surface.headsUp.default}
        >
          <CarouselSpacer />
          {items.map(item => (
            <CollectionListTile
              key={item.id}
              item={item}
              getCollectionInfo={getCollectionInfo}
              clickHandlers={clickHandlers}
              analytics={analytics}
              itemPositionIndex={item.positionIndex}
              itemSource={baseItemSource}
            />
          ))}
          <CarouselSpacer />
        </Carousel>
      </PaddedCollapsibleSectionHeader>
    </BorderlessSmartCardSection>
  );
};

type CollectionListTileProps = {
  item: {
    id: string;
    primary: string;
    secondary: string | null;
  };
  getCollectionInfo?: (collectionId: string) => Maybe<CollectionListSmartCardCollectionInfo>;
  clickHandlers?: HeadsUpClickHandlers;
  analytics?: HeadsUpCardRendererAnalytics;
  itemPositionIndex: number;
  itemSource: HeadsUpItemSource;
};

const CollectionListTile = observer(function CollectionListTile({
  item,
  getCollectionInfo,
  clickHandlers,
  analytics,
  itemPositionIndex,
  itemSource,
}: CollectionListTileProps) {
  const collectionInfo = getCollectionInfo?.(item.id) ?? undefined;
  const { subtitle, isLoaded } = useCollectionSubtitle({
    collectionInfo,
    fallback: item.secondary,
  });

  const { iconColor } = useThemedCollectionColors(item.id);

  const analyticsMeta = useMemo<HeadsUpCardItemMeta>(
    () => ({
      itemKind: "collection",
      itemId: item.id,
      itemTitle: item.primary,
      itemPositionIndex,
      itemSource,
    }),
    [item.id, item.primary, itemPositionIndex, itemSource]
  );

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      analytics?.onItemClick?.(analyticsMeta);
      const handler = clickHandlers?.collectionClick(item.id);
      handler?.(event);
    },
    [analytics, analyticsMeta, clickHandlers, item.id]
  );

  return (
    <CollectionTile type="button" onClick={handleClick}>
      <TileIcon $color={iconColor}>
        <MDSIcon kind={MDSIconKind.Collection} />
      </TileIcon>
      <TileBody>
        <NoteTitle
          title={item.primary}
          variant={MDSTextVariant.TextSm}
          weight={MDSTextWeight.SemiBold}
          overflow={MDSTextOverflow.Ellipsis}
          numberOfLines={1}
        >
          {item.primary}
        </NoteTitle>
        <CollectionSubtitle
          title={isWhitespace(subtitle) ? undefined : subtitle}
          variant={MDSTextVariant.TextS}
          color={MDSTextColor.Secondary}
          overflow={MDSTextOverflow.Ellipsis}
          numberOfLines={1}
          $isLoaded={isLoaded}
        >
          {subtitle}
        </CollectionSubtitle>
      </TileBody>
    </CollectionTile>
  );
});

function useCollectionSubtitle({
  collectionInfo,
  fallback,
}: {
  collectionInfo?: CollectionListSmartCardCollectionInfo;
  fallback: string | null;
}): { subtitle: string; isLoaded: boolean } {
  if (!collectionInfo) {
    const text = fallback?.trim();
    return { subtitle: text && text.length > 0 ? text : "\u00A0", isLoaded: true };
  }

  const sizeData = collectionInfo.sizeData;
  const count = sizeData?.data;
  const hasLoadedCount = !isUndefined(count);

  if (!hasLoadedCount) {
    return { subtitle: "\u00A0", isLoaded: false };
  }

  const description = pickDescription({ collectionInfo, fallback, count });
  return {
    subtitle: formatCollectionSubtitle({ count: count ?? 0, description }),
    isLoaded: true,
  };
}

function pickDescription({
  collectionInfo,
  fallback,
  count,
}: {
  collectionInfo?: CollectionListSmartCardCollectionInfo;
  fallback: string | null;
  count?: number;
}): string | null {
  const trimmedCollectionDescription = collectionInfo?.description?.trim();
  if (trimmedCollectionDescription) {
    return trimmedCollectionDescription;
  }

  const trimmedFallback = fallback?.trim();
  if (!trimmedFallback) {
    return null;
  }

  const countLabel = typeof count === "number" ? formatCount(count) : null;
  if (countLabel && trimmedFallback.toLowerCase() === countLabel.toLowerCase()) {
    return null;
  }

  return trimmedFallback;
}

function formatCollectionSubtitle({
  count,
  description,
}: {
  count: number;
  description: string | null;
}): string {
  const countLabel = formatCount(count);
  if (!description) {
    return countLabel;
  }

  return `${countLabel} ・${description}`;
}

function formatCount(count: number): string {
  return `${count} ${inflect("note", count)}`;
}

function isWhitespace(value: string) {
  return !value.trim();
}

const BorderlessSmartCardSection = styled(SmartCardSection)`
  padding-left: 0;
  padding-right: 0;
`;

const PaddedCollapsibleSectionHeader = styled(CollapsibleSectionHeader)`
  padding-left: ${({ theme }) => `calc(${theme.spacing["4"]} + ${theme.spacing["2"]})`};
  padding-right: ${({ theme }) => `calc(${theme.spacing["4"]} + ${theme.spacing["2"]})`};
`;

const Carousel = styled(MDSFadeHorizontalScroll)`
  gap: ${({ theme }) => theme.spacing["1"]};
  padding: ${({ theme }) => theme.spacing["1"]} 0;
`;

const CarouselSpacer = styled.div`
  height: 100%;
  width: ${({ theme }) => `calc(${theme.spacing["4"]} + ${theme.spacing["1"]})`};
  min-width: ${({ theme }) => `calc(${theme.spacing["4"]} + ${theme.spacing["1"]})`};

  flex-shrink: 0;
`;

const NoteTitle = styled(MDSText)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: 100%;
  text-align: left;
`;

const CollectionSubtitle = styled(MDSText)<{ $isLoaded: boolean }>`
  transition: opacity 0.16s ease-in-out;
  opacity: ${({ $isLoaded }) => ($isLoaded ? 1 : 0)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: 100%;
  text-align: left;
`;

const CollectionTile = styled.button`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 192px;
  width: 192px;
  height: 84px;
  padding: ${({ theme }) => theme.spacing["2"]} ${({ theme }) => theme.spacing["4"]};
  background-color: ${({ theme }) => theme.colors.background.button.chip.default};
  border-radius: 16px;
  border: none;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.background};

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.button.chip.hover};
  }

  &:active {
    background-color: ${({ theme }) => theme.colors.background.button.chip.hover};
  }
`;

const TileBody = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 4px;
  gap: 4px;
  min-width: 0;
  width: 100%;
`;

const TileIcon = styled.span<{ $color: string }>`
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  & .mds-icon {
    font-size: 18px;
    color: ${({ $color, theme }) => $color ?? theme.colors.icon.placeholder};
    transition: color 0.16s ease-in-out;
  }
`;

const scrollButtonClassName = css`
  z-index: ${ZIndex.AboveShadow} !important;
`;
